import type { ReactNode } from 'react';
import { useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import Button from '@/components/base/Button';
import Input from '@/components/base/Input';
import Tabs, { type TabItem } from '@/components/base/Tabs';
import Switch from '@/components/base/Switch';
import Toast, { type ToastType } from '@/components/base/Toast';
import { riyadhDistricts } from '@/mocks/districts';
import { laundryProfile } from '@/mocks/dashboard';

interface ToastState { type: ToastType; message: string }
type PendingStatus = 'pending' | 'approved' | 'rejected' | null;

interface SectionStatus {
  business: PendingStatus;
  location: PendingStatus;
  hours: PendingStatus;
  exec: PendingStatus;
}

const TABS: TabItem[] = [
  { id: 'basic', label: 'البيانات الأساسية' },
  { id: 'location', label: 'العنوان والتغطية' },
  { id: 'operation', label: 'أوقات العمل والتنفيذ' },
  { id: 'account', label: 'الحساب والأمان' },
];

interface DaySchedule {
  key: string;
  label: string;
  enabled: boolean;
  from: string;
  to: string;
}

const DEFAULT_WEEK: DaySchedule[] = [
  { key: 'sat', label: 'السبت', enabled: true, from: '09:00', to: '22:00' },
  { key: 'sun', label: 'الأحد', enabled: true, from: '09:00', to: '22:00' },
  { key: 'mon', label: 'الاثنين', enabled: true, from: '09:00', to: '22:00' },
  { key: 'tue', label: 'الثلاثاء', enabled: true, from: '09:00', to: '22:00' },
  { key: 'wed', label: 'الأربعاء', enabled: true, from: '09:00', to: '22:00' },
  { key: 'thu', label: 'الخميس', enabled: true, from: '09:00', to: '22:00' },
  { key: 'fri', label: 'الجمعة', enabled: false, from: '14:00', to: '22:00' },
];

function PendingBadge() {
  return (
    <span className="flex items-center gap-1 rounded-full bg-warning-100 px-2.5 py-1 text-[12px] font-medium text-warning-700">
      <i className="ri-time-line text-sm" aria-hidden="true"></i>
      بانتظار الموافقة
    </span>
  );
}

function ApprovedBadge() {
  return (
    <span className="flex items-center gap-1 rounded-full bg-success-50 px-2.5 py-1 text-[12px] font-medium text-success-700">
      <i className="ri-checkbox-circle-line text-sm" aria-hidden="true"></i>
      تمت الموافقة
    </span>
  );
}

function RejectedBadge({ reason }: { reason?: string }) {
  return (
    <div className="group relative flex items-center gap-1">
      <span className="flex items-center gap-1 rounded-full bg-danger-50 px-2.5 py-1 text-[12px] font-medium text-danger-600">
        <i className="ri-close-circle-line text-sm" aria-hidden="true"></i>
        مرفوض
      </span>
      {reason && (
        <div className="invisible absolute top-full z-10 mt-1 w-[200px] rounded-card border border-background-200 bg-white p-2 text-[12px] text-foreground-700 shadow-soft group-hover:visible">
          {reason}
        </div>
      )}
    </div>
  );
}

function SettingCard({ title, badge, children, onSave }: {
  title: string;
  badge?: ReactNode;
  children: ReactNode;
  onSave?: () => void;
}) {
  return (
    <div className="rounded-card border border-background-200 bg-white p-5 shadow-card">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="font-semibold text-[15px] text-foreground-950">{title}</h3>
        {badge}
      </div>
      {children}
      {onSave && (
        <div className="mt-5 border-t border-background-100 pt-5">
          <Button variant="cta" size="md" icon="ri-send-plane-line" onClick={onSave}>
            حفظ التعديلات
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SettingsPage() {
  const [toast, setToast] = useState<ToastState | null>(null);
  const [lang, setLang] = useState<'ar' | 'en'>('ar');
  const [activeTab, setActiveTab] = useState('basic');

  const [sectionStatus, setSectionStatus] = useState<SectionStatus>({
    business: null,
    location: null,
    hours: null,
    exec: null,
  });

  const [businessForm, setBusinessForm] = useState({
    name: laundryProfile.name,
    description: 'مغسلة راقية تقدم أفضل خدمات الغسيل والكوي في الرياض بأسعار تنافسية',
    phone: '0112345678',
    logo: '',
  });

  const [locationForm, setLocationForm] = useState({
    district: 'الياسمين',
    street: 'شارع أنس بن مالك',
    building: '12',
    coverage: ['الياسمين', 'النرجس', 'الربيع', 'الصحافة', 'حطين'],
  });

  const [weekSchedule, setWeekSchedule] = useState<DaySchedule[]>(DEFAULT_WEEK);
  const [execForm, setExecForm] = useState({ normal: 48, urgent: 12 });

  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');

  const hasPending = Object.values(sectionStatus).includes('pending');

  const submitSection = (section: keyof SectionStatus) => {
    setSectionStatus((prev) => ({ ...prev, [section]: 'pending' }));
    setToast({ type: 'info', message: 'تم إرسال طلب التعديل للإدارة — سيتم الرد قريباً' });
  };

  const changePassword = () => {
    if (!passwordForm.current) { setPasswordError('كلمة المرور الحالية مطلوبة'); return; }
    if (!passwordForm.next) { setPasswordError('كلمة المرور الجديدة مطلوبة'); return; }
    if (passwordForm.next !== passwordForm.confirm) { setPasswordError('كلمتا المرور غير متطابقتين'); return; }
    setPasswordError('');
    setPasswordForm({ current: '', next: '', confirm: '' });
    setToast({ type: 'success', message: 'تم تغيير كلمة المرور بنجاح' });
  };

  const toggleDay = (key: string) => {
    setWeekSchedule((prev) => prev.map((day) => (day.key === key ? { ...day, enabled: !day.enabled } : day)));
  };

  const updateDayTime = (key: string, field: 'from' | 'to', value: string) => {
    setWeekSchedule((prev) => prev.map((day) => (day.key === key ? { ...day, [field]: value } : day)));
  };

  const enabledDays = weekSchedule.filter((day) => day.enabled);

  const toggleCoverage = (district: string) => {
    setLocationForm((prev) => ({
      ...prev,
      coverage: prev.coverage.includes(district)
        ? prev.coverage.filter((d) => d !== district)
        : [...prev.coverage, district],
    }));
  };

  const renderSectionBadge = (section: keyof SectionStatus) => {
    const status = sectionStatus[section];
    if (status === 'pending') return <PendingBadge />;
    if (status === 'approved') return <ApprovedBadge />;
    if (status === 'rejected') return <RejectedBadge reason="يرجى مراجعة شروط الاستخدام" />;
    return null;
  };

  const isLocked = (section: keyof SectionStatus) => sectionStatus[section] === 'pending';

  return (
    <DashboardLayout title="الإعدادات">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="space-y-6">
        <h2 className="font-bold text-[18px] text-foreground-950">الإعدادات</h2>

        {hasPending && (
          <div className="flex items-center gap-3 rounded-card border border-warning-100 bg-warning-50 px-4 py-3">
            <i className="ri-time-line text-lg text-warning-600" aria-hidden="true"></i>
            <p className="text-[13px] font-medium text-warning-700">
              لديك طلب تعديل بانتظار مراجعة الإدارة — بعض الحقول مؤقتاً مقفلة
            </p>
          </div>
        )}

        <div className="rounded-card border border-background-200 bg-white px-3 shadow-card">
          <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />
        </div>

        {activeTab === 'basic' && (
          <SettingCard
            title="بيانات المغسلة"
            badge={renderSectionBadge('business')}
            onSave={isLocked('business') ? undefined : () => submitSection('business')}
          >
            <div className="space-y-4">
              <Input
                label="اسم المغسلة"
                name="laundryName"
                value={businessForm.name}
                onChange={(event) => setBusinessForm((prev) => ({ ...prev, name: event.target.value }))}
                disabled={isLocked('business')}
              />
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground-800">الوصف المختصر</label>
                <textarea
                  value={businessForm.description}
                  onChange={(event) => setBusinessForm((prev) => ({ ...prev, description: event.target.value }))}
                  disabled={isLocked('business')}
                  rows={3}
                  maxLength={500}
                  className="w-full resize-none rounded-field border border-background-300 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100 disabled:bg-background-50 disabled:text-foreground-400"
                  placeholder="وصف مختصر عن مغسلتك..."
                />
              </div>
              <Input
                label="رقم الهاتف"
                name="laundryPhone"
                value={businessForm.phone}
                onChange={(event) => setBusinessForm((prev) => ({ ...prev, phone: event.target.value }))}
                disabled={isLocked('business')}
              />
              <div>
                <p className="mb-1.5 text-[13px] font-medium text-foreground-800">الشعار / الصورة</p>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 overflow-hidden rounded-[12px] border border-background-200 bg-background-50">
                    <img src={laundryProfile.logo} alt="الشعار" className="h-full w-full object-cover" />
                  </div>
                  <button
                    type="button"
                    disabled={isLocked('business')}
                    className="flex items-center gap-2 rounded-btn border border-background-300 px-4 py-2.5 text-[13px] font-medium text-foreground-700 transition-colors hover:bg-background-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <i className="ri-upload-cloud-line text-base" aria-hidden="true"></i>
                    تغيير الصورة
                  </button>
                </div>
              </div>
            </div>
          </SettingCard>
        )}

        {activeTab === 'location' && (
          <SettingCard
            title="العنوان والتغطية"
            badge={renderSectionBadge('location')}
            onSave={isLocked('location') ? undefined : () => submitSection('location')}
          >
            <div className="space-y-4">
              <div className="h-[280px] overflow-hidden rounded-card border border-background-200 bg-background-100">
                <iframe
                  title="موقع المغسلة"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.6745226734097!2d46.7753676!3d24.7135676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2f03890d489399%3A0xba974d1c98e79fd5!2sRiyadh%20Saudi%20Arabia!5e0!3m2!1sen!2s!4v1695000000000!5m2!1sen!2s"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen={false}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="h-full w-full"
                ></iframe>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Input label="الحي" name="district" value={locationForm.district} onChange={(event) => setLocationForm((prev) => ({ ...prev, district: event.target.value }))} disabled={isLocked('location')} />
                <Input label="الشارع" name="street" value={locationForm.street} onChange={(event) => setLocationForm((prev) => ({ ...prev, street: event.target.value }))} disabled={isLocked('location')} />
                <Input label="رقم المبنى" name="building" value={locationForm.building} onChange={(event) => setLocationForm((prev) => ({ ...prev, building: event.target.value }))} disabled={isLocked('location')} />
              </div>
              <div>
                <p className="mb-2.5 text-[13px] font-medium text-foreground-800">أحياء التغطية</p>
                <div className="flex flex-wrap gap-2">
                  {riyadhDistricts.map((district) => {
                    const isSelected = locationForm.coverage.includes(district);
                    return (
                      <button
                        key={district}
                        type="button"
                        disabled={isLocked('location')}
                        onClick={() => toggleCoverage(district)}
                        className={[
                          'rounded-full px-3 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                          isSelected
                            ? 'bg-primary-500 text-white'
                            : 'border border-background-300 bg-white text-foreground-700 hover:bg-background-50',
                        ].join(' ')}
                      >
                        {district}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </SettingCard>
        )}

        {activeTab === 'operation' && (
          <div className="space-y-6">
            <SettingCard
              title="أوقات العمل"
              badge={renderSectionBadge('hours')}
              onSave={isLocked('hours') ? undefined : () => submitSection('hours')}
            >
              <div className="space-y-4">
                {enabledDays.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 rounded-card border border-secondary-100 bg-secondary-50 px-4 py-3">
                    <span className="flex items-center gap-1.5 text-[12px] font-medium text-secondary-700">
                      <i className="ri-calendar-check-line text-base" aria-hidden="true"></i>
                      أيام العمل الحالية:
                    </span>
                    {enabledDays.map((day) => (
                      <span
                        key={day.key}
                        className="rounded-full border border-secondary-200/70 bg-white px-2.5 py-1 text-[12px] font-medium text-foreground-700"
                      >
                        {day.label}{' '}
                        <span className="text-foreground-400">{day.from} — {day.to}</span>
                      </span>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  {weekSchedule.map((day) => (
                    <div
                      key={day.key}
                      className={[
                        'flex flex-col gap-3 rounded-card border px-4 py-3 transition-colors sm:flex-row sm:items-center sm:justify-between',
                        day.enabled
                          ? 'border-background-200 bg-white'
                          : 'border-background-100 bg-background-50',
                      ].join(' ')}
                    >
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={day.enabled}
                          onChange={() => toggleDay(day.key)}
                          disabled={isLocked('hours')}
                          size="sm"
                        />
                        <span
                          className={[
                            'w-16 text-[14px] font-medium transition-colors',
                            day.enabled ? 'text-foreground-950' : 'text-foreground-400',
                          ].join(' ')}
                        >
                          {day.label}
                        </span>
                        {!day.enabled && (
                          <span className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] font-medium text-foreground-400">
                            مغلق
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={day.from}
                          aria-label={`${day.label} — من الساعة`}
                          onChange={(event) => updateDayTime(day.key, 'from', event.target.value)}
                          disabled={isLocked('hours') || !day.enabled}
                          className="h-10 w-[120px] rounded-field border border-background-300 px-3 text-sm text-foreground-900 transition-colors focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100 disabled:cursor-not-allowed disabled:bg-background-50 disabled:text-foreground-300"
                        />
                        <span className="text-[13px] text-foreground-400">إلى</span>
                        <input
                          type="time"
                          value={day.to}
                          aria-label={`${day.label} — إلى الساعة`}
                          onChange={(event) => updateDayTime(day.key, 'to', event.target.value)}
                          disabled={isLocked('hours') || !day.enabled}
                          className="h-10 w-[120px] rounded-field border border-background-300 px-3 text-sm text-foreground-900 transition-colors focus:border-secondary-400 focus:outline-none focus:ring-2 focus:ring-secondary-100 disabled:cursor-not-allowed disabled:bg-background-50 disabled:text-foreground-300"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SettingCard>

            <SettingCard
              title="مدة التنفيذ"
              badge={renderSectionBadge('exec')}
              onSave={isLocked('exec') ? undefined : () => submitSection('exec')}
            >
              <div className="flex flex-wrap gap-6">
                {([['normal', 'عادي', 'text-foreground-700'], ['urgent', 'مستعجل', 'text-accent-700']] as const).map(
                  ([key, label, color]) => (
                    <div key={key} className="flex items-center gap-3">
                      <span className={`text-[14px] font-medium ${color}`}>{label}:</span>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={1}
                          max={240}
                          value={execForm[key]}
                          onChange={(event) =>
                            setExecForm((prev) => ({ ...prev, [key]: Number(event.target.value) }))
                          }
                          disabled={isLocked('exec')}
                          className="h-11 w-20 rounded-field border border-background-300 px-3 text-center text-[15px] font-semibold text-foreground-950 focus:border-secondary-400 focus:outline-none disabled:bg-background-50"
                        />
                        <span className="text-[13px] text-foreground-500">ساعة</span>
                      </div>
                    </div>
                  )
                )}
              </div>
            </SettingCard>
          </div>
        )}

        {activeTab === 'account' && (
          <div className="space-y-6">
            <SettingCard title="الحساب">
              <div className="space-y-5">
                <div>
                  <h4 className="mb-3 font-medium text-[14px] text-foreground-900">تغيير كلمة المرور</h4>
                  <div className="space-y-3">
                    <Input label="كلمة المرور الحالية" name="currentPassword" type="password" value={passwordForm.current} onChange={(event) => setPasswordForm((prev) => ({ ...prev, current: event.target.value }))} placeholder="••••••••" />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Input label="كلمة المرور الجديدة" name="newPassword" type="password" value={passwordForm.next} onChange={(event) => { setPasswordForm((prev) => ({ ...prev, next: event.target.value })); setPasswordError(''); }} placeholder="••••••••" />
                      <Input label="تأكيد كلمة المرور" name="confirmPassword" type="password" value={passwordForm.confirm} onChange={(event) => { setPasswordForm((prev) => ({ ...prev, confirm: event.target.value })); setPasswordError(''); }} placeholder="••••••••" error={passwordError} />
                    </div>
                    <Button variant="primary" size="md" icon="ri-lock-password-line" onClick={changePassword}>
                      تغيير كلمة المرور
                    </Button>
                  </div>
                </div>

                <div className="border-t border-background-100 pt-5">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h4 className="font-medium text-[14px] text-foreground-900">لغة اللوحة</h4>
                      <p className="mt-0.5 text-[12px] text-foreground-400">يتطبق فوراً دون مراجعة</p>
                    </div>
                    <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-50 p-1">
                      {(['ar', 'en'] as const).map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setLang(l)}
                          className={[
                            'rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors',
                            lang === l ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:text-foreground-700',
                          ].join(' ')}
                        >
                          {l === 'ar' ? 'العربية' : 'English'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </SettingCard>

            <div className="pb-4">
              <Button
                variant="danger"
                size="lg"
                fullWidth
                icon="ri-logout-box-r-line"
                onClick={() => setToast({ type: 'info', message: 'تم تسجيل الخروج' })}
              >
                تسجيل الخروج
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}